import React, { useState, useEffect } from 'react';
import { Trash2, Clock, Info } from 'lucide-react';
import ColorSwatchPicker from './ColorSwatchPicker';

interface CountdownBlockData {
  endDate: string;
  title: string;
  textColor: string;
  backgroundColor: string;
}

interface CountdownBlockProps {
  data: CountdownBlockData;
  onChange: (data: CountdownBlockData) => void;
  onDelete: () => void;
  isPreview?: boolean;
}

const CountdownBlock: React.FC<CountdownBlockProps> = ({ data, onChange, onDelete, isPreview }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calculateTimeLeft = () => {
      // If no endDate set, use fallback countdown
      if (!data.endDate) {
        // Fallback: loop 28:59 minutes countdown
        const now = new Date().getTime();
        const seconds = Math.floor((now / 1000) % 1739); // 1739 seconds = 28:59 minutes
        const remaining = 1739 - seconds;
        
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: Math.floor(remaining / 60),
          seconds: remaining % 60
        });
        return;
      }

      const difference = new Date(data.endDate).getTime() - new Date().getTime();
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      } else {
        // After endDate expired, use fallback countdown loop
        const now = new Date().getTime();
        const seconds = Math.floor((now / 1000) % 1739); // 1739 seconds = 28:59 minutes
        const remaining = 1739 - seconds;
        
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: Math.floor(remaining / 60),
          seconds: remaining % 60
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [data.endDate]);

  if (isPreview) {
    return (
      <div 
        className="rounded-xl p-8 text-center"
        style={{ backgroundColor: data.backgroundColor, color: data.textColor }}
      >
        <div className="flex items-center justify-center gap-2 mb-4">
          <Clock className="w-6 h-6" />
          <h3 className="text-2xl font-bold">{data.title || 'โปรโมชั่นพิเศษ!'}</h3>
        </div>
        
        <p className="mb-6 text-lg opacity-90">เหลือเวลาอีก</p>
        
        <div className="grid grid-cols-4 gap-4 max-w-2xl mx-auto">
          <div className="bg-white bg-opacity-20 rounded-lg p-4 backdrop-blur-sm">
            <div className="text-4xl font-bold">{timeLeft.days}</div>
            <div className="text-sm opacity-80 mt-1">วัน</div>
          </div>
          <div className="bg-white bg-opacity-20 rounded-lg p-4 backdrop-blur-sm">
            <div className="text-4xl font-bold">{timeLeft.hours}</div>
            <div className="text-sm opacity-80 mt-1">ชั่วโมง</div>
          </div>
          <div className="bg-white bg-opacity-20 rounded-lg p-4 backdrop-blur-sm">
            <div className="text-4xl font-bold">{timeLeft.minutes}</div>
            <div className="text-sm opacity-80 mt-1">นาที</div>
          </div>
          <div className="bg-white bg-opacity-20 rounded-lg p-4 backdrop-blur-sm">
            <div className="text-4xl font-bold">{timeLeft.seconds}</div>
            <div className="text-sm opacity-80 mt-1">วินาที</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900">นับถอยหลัง</h3>
          <div className="relative">
            <Info className="w-4 h-4 text-gray-400 cursor-help peer" />
            <div className="absolute left-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible peer-hover:opacity-100 peer-hover:visible transition-all duration-200 z-10 shadow-xl pointer-events-none">
              <p className="font-semibold mb-1">สร้างความเร่งด่วน</p>
              <p className="text-gray-300">แสดงตัวนับถอยหลังเพื่อกระตุ้นให้ลูกค้าตัดสินใจเร็วขึ้น เหมาะสำหรับโปรโมชั่นจำกัดเวลา</p>
            </div>
          </div>
        </div>
        <button
          onClick={onDelete}
          className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">หัวข้อ</label>
          <div className="flex items-center gap-2">
            <ColorSwatchPicker
              compact
              color={data.textColor}
              fallback="#ffffff"
              onChange={(hex) => onChange({ ...data, textColor: hex })}
            />
            <input
              type="text"
              value={data.title}
              onChange={(e) => onChange({ ...data, title: e.target.value })}
              placeholder="โปรโมชั่นพิเศษ!"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">วันที่สิ้นสุด</label>
          <input
            type="datetime-local"
            value={data.endDate}
            onChange={(e) => onChange({ ...data, endDate: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
        </div>

        <div className="flex items-center gap-2">
          <ColorSwatchPicker
            compact
            color={data.backgroundColor}
            onChange={(hex) => onChange({ ...data, backgroundColor: hex })}
          />
          <span className="text-sm font-medium text-gray-700">สีพื้นหลัง</span>
        </div>

        <div className="pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600 mb-2">ตัวอย่าง:</p>
          <div 
            className="rounded-xl p-6 text-center"
            style={{ backgroundColor: data.backgroundColor, color: data.textColor }}
          >
            <div className="flex items-center justify-center gap-2 mb-3">
              <Clock className="w-5 h-5" />
              <h3 className="text-lg font-bold">{data.title || 'โปรโมชั่นพิเศษ!'}</h3>
            </div>
            <p className="mb-4 opacity-90">เหลือเวลาอีก</p>
            <div className="grid grid-cols-4 gap-2">
              <div className="bg-white bg-opacity-20 rounded-lg p-2 backdrop-blur-sm">
                <div className="text-2xl font-bold">{timeLeft.days}</div>
                <div className="text-xs opacity-80">วัน</div>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-2 backdrop-blur-sm">
                <div className="text-2xl font-bold">{timeLeft.hours}</div>
                <div className="text-xs opacity-80">ชั่วโมง</div>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-2 backdrop-blur-sm">
                <div className="text-2xl font-bold">{timeLeft.minutes}</div>
                <div className="text-xs opacity-80">นาที</div>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-2 backdrop-blur-sm">
                <div className="text-2xl font-bold">{timeLeft.seconds}</div>
                <div className="text-xs opacity-80">วินาที</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountdownBlock;
